import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthorizeService } from '@bds/auth';
import { ClmSightCodeService, CommodityShippedService } from '@bds/data-access';
import { Equipment, EquipmentService } from '@bds/equipment';
import {
    RtClm,
    RtClmSightCode,
    RtClmView,
    RtCommodityShipped,
    RtTrip,
    RtvTripError,
} from '@bds/railtrac-models';
import { faList, faPlus } from '@fortawesome/pro-light-svg-icons';
import {
    faAddressBook,
    faArrowRotateRight,
    faCodeBranch,
    faEllipsisVAlt,
    faExternalLink,
    faFileImport,
    faFlaskPotion,
    faHandHoldingUsd,
    faInfo,
    faRoute,
    faSave,
    faWrench,
} from '@fortawesome/pro-solid-svg-icons';
import { faBars, faSpinner, faRepeat } from '@fortawesome/pro-regular-svg-icons';
import {
    faCommentAltPlus,
    faObjectGroup,
    faPencilAlt,
    faTrashAlt,
} from '@fortawesome/pro-duotone-svg-icons';
import { faClmHistory, faTrain } from '@bds/fa-svg-icons';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { ScrollSpyService } from '@bds/helpers';
import { CommunicationMessage, CommunicationService } from '@bds/core';
import { map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { TripCarHotListDto } from '@bds/railtrac-models';
import { RailtracClmService } from '../railtrac-clm/railtrac-clm.service';
import { ClmFunctionsService, CreateTripService, RtTripErrorService } from '@bds/railtrac';
import { EquipmentApplicationPathProvider } from '@bds/equipment-pathing';
import { RtClmComponent } from '../rt-clm/rt-clm.component';
import {
    RtTripCommodityShippedDialogComponent,
    RtTripCommodityShippedDialogOptions,
} from '../railtrac-trip/rt-trip-commodity-shipped/rt-trip-commodity-shipped-dialog/rt-trip-commodity-shipped-dialog.component';
import { RtTripErrorDetailsComponent } from './rt-trip-error-details/rt-trip-error-details.component';

export class TripErrorIcons {
    add = faPlus;
    reprocess = faArrowRotateRight;
    import = faFileImport;
    commodity = faFlaskPotion;
    customer = faAddressBook;
    detail = faInfo;
    detention = faHandHoldingUsd;
    diversion = faCodeBranch;
    group = faObjectGroup;
    history = faClmHistory;
    list = faList;
    menu = faBars;
    popout = faExternalLink;
    profile = faWrench;
    route = faRoute;
    save = faSave;
    saveAndCreateNew = faRepeat;
    spinner = faSpinner;
    train = faTrain;
    verticalEllipsis = faEllipsisVAlt;
}

@Component({
    selector: 'rt-trip-error',
    templateUrl: './rt-trip-error.component.html',
    styleUrls: ['./rt-trip-error.component.scss'],
})
export class RtTripErrorComponent implements OnInit, OnChanges, OnDestroy {
    alteredTrip: RtTrip;
    alteredTripRefs: RtTrip;
    carId = '';
    clms: RtClm[] = [];
    currentClm: RtClmView;
    commoditiesShipped: RtCommodityShipped[] = [];
    compartmentCommodities: RtCommodityShipped[] = [];
    compartmentCommoditiesModified: RtCommodityShipped[] = [];
    disabledClmFields: Array<string> | string = [`sightCode`];
    equipment: Equipment = <Equipment>{};
    errorMessage = '';
    hotLists: TripCarHotListDto[];
    icons = new TripErrorIcons();
    isDirty = false;
    isSaving = false;
    isTripValid = false;
    canAddImport = true;
    canReProcess = true;
    canUpdateInterfaceValidation = false;
    sightCodes: Observable<RtClmSightCode[]>;
    unassignedCommodities: RtCommodityShipped[] = [];
    userName: string;
    routeReadOnly = true;
    isReapplyClms = false;
    tripError: RtvTripError;

    @Input() ormId: number;
    @ViewChild('tripErrorDetails') tripErrorDetails: RtTripErrorDetailsComponent;
    @ViewChild('clmPanel') clmPanel: MatExpansionPanel;
    @ViewChild('custPanel') custPanel: MatExpansionPanel;
    @ViewChild('routePanel') routePanel: MatExpansionPanel;

    @ViewChild(RtClmComponent) clmComponent: RtClmComponent;
    private unsub$ = new Subject<void>();

    iconEdit = faPencilAlt;
    iconAdd = faCommentAltPlus;
    iconDelete = faTrashAlt;

    constructor(
        private tripErrorService: RtTripErrorService,
        private equipmentService: EquipmentService,
        private _snackbar: MatSnackBar,
        public scrollSpyService: ScrollSpyService,
        private clmService: RailtracClmService,
        private authService: AuthorizeService,
        private clmSightCodeService: ClmSightCodeService,
        public communicationService: CommunicationService,
        public createTripService: CreateTripService,
        public dialog: MatDialog,
        private commodityService: CommodityShippedService,
        public equipmentAppPathProvider: EquipmentApplicationPathProvider,
        private clmFunctionsService: ClmFunctionsService,
        private router: Router,
    ) {}

    /**
     * set selected section
     * @param section string
     */
    setCurrentSection(section: string): void {
        this.scrollSpyService.changeSection(section);
    }

    openPanel(panel: MatExpansionPanel | undefined, section: string): void {
        panel?.open();
        this.setCurrentSection(section);
    }

    async ngOnChanges(changes: SimpleChanges) {
        const tripIdChange = changes['ormId'];
        if (tripIdChange) {
            await firstValueFrom(this.getTripError());
            this.canAddImport = true;
            this.canReProcess = true;
            this.canUpdateInterfaceValidation = false;
        }
    }

    ngOnDestroy(): void {
        this.unsub$.next();
        this.unsub$.complete();
    }

    ngOnInit() {
        this.authService
            .getUser()
            .pipe(
                takeUntil(this.unsub$),
                map((u) => u && u['preferred_username']),
                tap((username) => {
                    this.userName = username;
                }),
            )
            .subscribe();

        this.sightCodes = this.clmSightCodeService.getAll();
    }

    onEquipmentChange(ormId: number | null): void {
        if (ormId) {
            this.equipmentService.read(ormId).subscribe((eq) => {
                this.equipment = eq;
            });
        } else {
            this.equipment = <Equipment>{};
        }
    }

    getEquipment(carInit: string, carNo: string): Observable<Equipment> {
        return this.equipmentService.getOrmIdForEquipmentId(carInit, carNo).pipe(
            switchMap((ormId) => this.equipmentService.read(ormId)),
            tap((eq) => {
                this.equipment = eq;
            }),
        );
    }

    getTripError(): Observable<RtvTripError> {
        return this.tripErrorService.read(this.ormId).pipe(
            tap((trip) => {
                this.tripError = trip;
                this.getEquipment(trip.carInit, trip.carNo).pipe(take(1)).subscribe();
            }),
        );
    }

    /**
     * Used in conjunction with railtrac-trip-details->@Output() formValidChange.
     * Keeps parent component in sync with the validity of a trip.
     * @param isValid Is the child trip valid.
     */
    onFormValidChange(isValid: boolean): void {
        this.isTripValid = isValid;
    }

    async reloadClms(): Promise<void> {
        //: CLM firing off reload means recalc was called and the trip should be reloaded.
        await firstValueFrom(this.getClms());
    }

    getClms(): Observable<RtClm[]> {
        return this.clmService.getClmsForTrip(this.ormId ?? this.tripError.ormId).pipe(
            tap((c) => {
                this.clms = c;
            }),
        );
    }

    onTripValuesChange($event: RtTrip): void {
        this.alteredTripRefs = $event;
    }

    onCustNoChange(custNo: string): void {
        if (this.tripError) {
            this.tripError.custNo = custNo;
        }
    }

    onFormDirtyChange(isDirty: boolean): void {
        if (isDirty) {
            this.canUpdateInterfaceValidation = true;
            this.canAddImport = false;
            this.canReProcess = false;
        }
    }

    setTripRefs(alteredTrip: RtTrip): RtTrip {
        Object.keys(this.alteredTripRefs)
            .filter((key) => /trip_ref.*/.test(key))
            .forEach((key) => {
                alteredTrip[key] = this.alteredTripRefs[key];
            });

        return alteredTrip;
    }

    reProcess(): void {
        this.isSaving = true;
        this.tripErrorService.reProcess([this.tripError.ormId]).subscribe({
            next: (result) => {
                this.isSaving = false;
                if (result && result.relatedObject.length === 0) {
                    this._snackbar.open('Trip(s) reprocessed successfully.', 'Ok', {
                        duration: 3000,
                    });
                    const message: CommunicationMessage = {
                        subscriberName: 'TripErrorScreen',
                        action: 'reprocess',
                        data: result.relatedObject,
                    };
                    this.communicationService.sendData(message);

                    void this.router.navigate(['interface-validation'], {
                        queryParamsHandling: 'preserve',
                    });
                } else {
                    this._snackbar.open(
                        `Reprocess failed for trip(s) ${result.relatedObject
                            .map((o) => o.carInit + o.carNo)
                            .join(', ')}.`,
                        'Info',
                        {
                            duration: 3000,
                        },
                    );
                    const message: CommunicationMessage = {
                        subscriberName: 'TripErrorScreen',
                        action: 'reprocess',
                        data: result.relatedObject,
                    };
                    this.tripErrorDetails.tripForm.patchValue({
                        errorDescription: result.relatedObject[0].errorDescription,
                    });
                    this.communicationService.sendData(message);
                }
            },
            error: (err) => {
                this.isSaving = false;
                this._snackbar.open('Error reprocessing trip(s).', 'Error', {
                    duration: 3000,
                });
            },
            complete: () => {
                this.isSaving = false;
            },
        });
    }

    addImport(): void {
        const importTrip: RtvTripError = this.tripErrorDetails.tripForm.getRawValue();
        importTrip.origRoad = null;
        importTrip.dlvRoad = null;
        this.isSaving = true;
        this.tripErrorService.addImport(importTrip).subscribe({
            next: (trip) => {
                this.isSaving = false;
                this._snackbar.open('Trip import added successfully.', 'Ok', {
                    duration: 3000,
                });
                const message: CommunicationMessage = {
                    subscriberName: 'TripErrorScreen',
                    action: 'reprocess',
                    data: trip,
                };
                this.communicationService.sendData(message);
                void this.router.navigate(['interface-validation'], {
                    queryParamsHandling: 'preserve',
                });
            },
            error: (err) => {
                this.isSaving = false;
                this._snackbar.open('Error adding trip import.', 'Error', {
                    duration: 3000,
                });
                console.error(err);
            },
            complete: () => {
                this.isSaving = false;
            },
        });
    }

    updateInterfaceValidation(): void {
        const importTrip: RtvTripError = this.tripErrorDetails.tripForm.getRawValue();
        this.isSaving = true;
        this.tripErrorService.updateInterfaceValidation(importTrip).subscribe({
            next: (trip) => {
                this.isSaving = false;
                this._snackbar.open('Interface validation updated successfully.', 'Ok', {
                    duration: 3000,
                });
                const message: CommunicationMessage = {
                    subscriberName: 'TripErrorScreen',
                    action: 'updateInterfaceValidation',
                    data: trip,
                };
                this.communicationService.sendData(message);
                this.canUpdateInterfaceValidation = false;
                this.canAddImport = true;
                this.canReProcess = true;
                if (this.tripErrorDetails?.tripForm) {
                    this.tripErrorDetails.isInitializing = true;
                    Promise.resolve().then(() => {
                        this.tripErrorDetails.tripForm.markAsPristine();
                        this.tripErrorDetails.isInitializing = false;
                    });
                }
            },
            error: (err: any) => {
                this.isSaving = false;
                this._snackbar.open('Error updating interface validation.', 'Error', {
                    duration: 3000,
                });
                console.error(err);
            },
            complete: () => {
                this.isSaving = false;
            },
        });
    }

    scroll(container: HTMLElement, component?: MatExpansionPanel): void {
        container.scrollIntoView({ behavior: 'smooth' } as ScrollIntoViewOptions);
        if (component instanceof MatExpansionPanel) {
            component.open();
        }
    }

    onResolveTripOptions(e: string): void {
        switch (e) {
            case 'newTrip':
                this.createTripService.emitCreateTripChange('');
                break;
            case 'addComment':
                // this.commentsComponent.onSaved('Comment Saved');
                break;
            case 'addDestinationCriteria':
                this.createTripService.emitRefreshTripGridChange(true);
                break;
            case 'reapplyClms':
                //navigate to CLM sectionqt
                this.openPanel(this.clmPanel, 'clmAnchor');

                if (this.clmComponent) {
                    //component initialized
                    this.clmFunctionsService.emitReapplyClmChange(true);
                } else {
                    //component not initialized - set flag and wait for component to load
                    this.isReapplyClms = true;
                }
                break;
            default:
                break;
        }
    }

    onAddProductClick(): void {
        const product = new RtCommodityShipped();
        product.carInit = this.tripError.carInit;
        product.carNo = this.tripError.carNo;
        product.rtTripOrmId = this.tripError.ormId;
        product.updateDateTime = new Date();
        product.shipDate = this.tripError.shipDateTime;
        product.userId = this.userName;
        if (this.commoditiesShipped) {
            product.commodityCompart = this.commoditiesShipped.length + 1;
        } else {
            product.commodityCompart = 1;
        }

        const y: RtTripCommodityShippedDialogOptions = {
            product: product,
            productOp: 'add',
            multiTrip: false,
        };

        const dialogRef = this.dialog.open(RtTripCommodityShippedDialogComponent, {
            width: '800px',
            data: y,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.commodityService
                    .add(result)
                    .pipe(take(1))
                    .subscribe(
                        () => {
                            // this.reloadCommodityShipped();
                            this._snackbar.open('Product Saved.', 'Ok');
                        },
                        () => {
                            this._snackbar.open('Error during save.', 'Ok');
                        },
                    );
            }
        });
    }

    onDeleteProductClick(ormId: number): void {
        this.commodityService
            .delete(ormId)
            .pipe(take(1))
            .subscribe(
                () => {
                    this._snackbar.open('Product deleted.', 'Ok');
                    // this.reloadCommodityShipped(true);
                    //this.verifyCompartNumbers();
                },
                () => {
                    this._snackbar.open('Error during delete.', 'Ok');
                },
            );
    }

    verifyCompartNumbers(): void {
        let idx = 1;
        this.commoditiesShipped.forEach((child) => {
            if (child.commodityCompart == idx) {
                idx++;
            } else {
                child.commodityCompart = idx;
                this.commoditiesShipped[idx - 1] = child;
                this.commodityService
                    .edit(child)
                    .pipe(take(1))
                    .subscribe(() => {
                        // this.reloadCommodityShipped();
                    });
            }
        });
    }
}
